/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React from 'react';
import { Box, FormControlLabel, Radio, FormControl, TextField, InputAdornment, IconButton } from '@mui/material';
import { MonthlyPatternTypes } from '../../AllSchedules/AllSchedules.Model';
import { Icons } from '../../../../../Shared/Constants/Icons';
import { isEmpty } from 'lodash';
import { ScheduleDaysContainer } from '../../AllSchedules/AllJobs.Style';
import theme from '../../../../../Shared/Themes/theme';
import type { IMonthlyScheduleProps } from './CreateSchedule.Model';

import { Options } from '../../../../../Shared/Components/Common/Dropdown/Dropdown.styles';
import { Styles } from './CreateSchedules.Style';
import { buttonStyles } from '../../../../Jobs/Components/AddEditOneTimeJob/AddEditOneTimeJob.Style';
import { BoxShadow } from '../../../../../Shared/Constants/Style.Constants';

const MonthlySchedule: React.FC<IMonthlyScheduleProps> = ({
  t,
  monthlyPatternType,
  setValue,
  radioButton,
  radioButtonText,
  quarterBoxStyle,
  selectedDays,
  handleDayClick,
  selectedWeek,
  setSelectedWeek,
  isWeekOpen,
  setIsWeekOpen,
  WeekOptions,
  selectedDayOfWeek,
  setSelectedDayOfWeek,
  isDayOfWeekOpen,
  setDayOfWeekOpen,
  DayOptions,
  daysStyle,
  Labels,
}) => {
  const { marginBottom } = Styles;

  const { textField } = buttonStyles;

  return (
    <>
      <Box sx={{ ...quarterBoxStyle, gap: '1.5rem' }}>
        {Object.values(MonthlyPatternTypes).map((type) => (
          <FormControl component="fieldset" sx={{ width: '22.5rem', minWidth: '5rem' }}>
            <FormControlLabel
              key={type}
              control={
                <Radio
                  checked={monthlyPatternType === type}
                  onChange={() => setValue('monthlyPatternType', type)}
                  sx={radioButton}
                />
              }
              label={type}
              sx={radioButtonText}
            />
          </FormControl>
        ))}
      </Box>

      {!isEmpty(monthlyPatternType) && (
        <>
          {monthlyPatternType === MonthlyPatternTypes.Weekly && (
            <Box sx={{ display: 'flex', mt: 2 }}>
              {/* Week Selector */}
              <Box>
                <TextField
                  select
                  sx={{ ...textField, marginBottom: '2rem' }}
                  label={
                    <span>
                      {t?.('jobManagement:week')} <span className="mandatory-field">*</span>
                    </span>
                  }
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(e?.target?.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            setIsWeekOpen(!isWeekOpen);
                          }}
                          style={{
                            padding: 0,
                          }}>
                          <img
                            src={isWeekOpen ? Icons.DropdownDownIcon : Icons.DropdownUpIcon}
                            alt={t?.(Labels.TextDropDown)}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  SelectProps={{
                    open: isWeekOpen,
                    IconComponent: () => null,
                    onOpen: () => setIsWeekOpen(true),
                    onClose: () => setIsWeekOpen(false),
                    MenuProps: {
                      PaperProps: {
                        style: {
                          boxShadow: BoxShadow.BoxShadow,
                        },
                      },
                    },
                  }}>
                  {WeekOptions?.map((week) => (
                    <Options key={week.value} value={week.value}>
                      {week.label}
                    </Options>
                  ))}
                </TextField>
              </Box>

              {/* Day-of-Week Selector */}
              <Box>
                <TextField
                  select
                  sx={{ ...textField, marginBottom: '2rem' }}
                  label={
                    <span>
                      {t?.('jobManagement:day')} <span className="mandatory-field">*</span>
                    </span>
                  }
                  value={selectedDayOfWeek.toLowerCase?.() || ''}
                  onChange={(e) => setSelectedDayOfWeek(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            setDayOfWeekOpen(!isDayOfWeekOpen);
                          }}
                          style={{
                            padding: 0,
                          }}>
                          <img
                            src={isDayOfWeekOpen ? Icons.DropdownDownIcon : Icons.DropdownUpIcon}
                            alt={t?.(Labels.TextDropDown)}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  SelectProps={{
                    open: isDayOfWeekOpen,
                    IconComponent: () => null,
                    onOpen: () => setDayOfWeekOpen(true),
                    onClose: () => setDayOfWeekOpen(false),
                    MenuProps: {
                      PaperProps: {
                        style: {
                          boxShadow: BoxShadow.BoxShadow,
                        },
                      },
                    },
                  }}>
                  {DayOptions?.map((day) => (
                    <Options key={day.value} value={day.value}>
                      {day?.label}
                    </Options>
                  ))}
                </TextField>
              </Box>
            </Box>
          )}
          {monthlyPatternType === MonthlyPatternTypes.SpecificDates && (
            <ScheduleDaysContainer sx={{ ...marginBottom }}>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((date) => (
                <Box
                  key={date}
                  onClick={() => handleDayClick(date.toString())}
                  bgcolor={selectedDays.includes(date.toString()) ? theme.palette.secondary.dark : ''}
                  fontWeight={
                    selectedDays.includes(date.toString())
                      ? theme.typography.fontWeightBold
                      : theme.typography.fontWeightLight
                  }
                  sx={daysStyle}>
                  {date}
                </Box>
              ))}
            </ScheduleDaysContainer>
          )}
        </>
      )}
    </>
  );
};

export default MonthlySchedule;
